import { useEffect, useState, useRef } from 'react';
import { isEmpty } from 'lodash';

import { useEnvStore, useUserStore } from '../../store/models';
import { SignInSignUpState } from '../../utils/constants';
import useOTPError from './useOTPError';

const TIME_REMAINING = 180;
const EMAIL_OTP_SENT_MESSAGE = 'A new verification code has been sent to your email.';
const PHONE_OTP_SENT_MESSAGE = 'A new verification code has been sent to your phone number.';

const EMAIL_SUBTITLE = 'Please enter the verification code that has been sent to your email.';
const getPhoneSubtitle = (phoneNumber) => `Please enter the verification code that has been sent to your phone number ${phoneNumber}.`;

/**
 * Resolves the phone number to display in the OTP dialog.
 * - SMS MFA challenge: uses the masked destination from Cognito (e.g. "+1******1234")
 * - Phone number verification: uses the full number from the user's attributes
 */
const resolveOtpPhoneNumber = (deliveryDetails, user) => {
  const isSmsDelivery = deliveryDetails?.deliveryMedium === 'SMS';
  const smsDeliveryPhone = deliveryDetails?.destination ?? null;
  const userAttributePhone = user?.userAttributes?.phone_number ?? null;
  return isSmsDelivery ? smsDeliveryPhone : userAttributePhone;
};

const RESENDING_STATES = new Set([
  SignInSignUpState.OTP_RESENDING,
  SignInSignUpState.OTP_SENDING,
]);

const RESEND_COMPLETE_STATES = new Set([
  SignInSignUpState.OTP_RESEND,
  SignInSignUpState.OTP_SENT,
]);

const VERIFYING_STATES = new Set([
  SignInSignUpState.OTP_CONFIRMING,
  SignInSignUpState.OTP_VERIFYING,
]);

const VERIFY_COMPLETE_STATES = new Set([
  SignInSignUpState.OTP_CONFIRM_SUCCESS,
  SignInSignUpState.OTP_VERIFIED,
]);

const VERIFY_ERROR_ACTIONS = new Set([
  'confirmSignIn',
  'confirmPhoneNumberVerification',
]);

const RESEND_ERROR_ACTIONS = new Set([
  'resendForOTP',
  'sendPhoneNumberVerificationCode',
  'updatePhoneNumber',
]);

/**
 * Custom hook to manage OTP verification state and logic
 * @param {boolean} open - Whether the OTP dialog is open
 * @param {Function} onVerify - Callback function to verify OTP
 * @param {Function} onResendOTP - Callback function to resend OTP
 * @returns {Object} State and handlers for OTP verification
 */
export const useVerifyOTP = (open, onVerify, onResendOTP) => {
  const envStore = useEnvStore();
  const userStore = useUserStore();
  const deliveryDetails = envStore.signInSignUpState.codeDeliveryDetails;
  const phoneNumber = resolveOtpPhoneNumber(deliveryDetails, userStore.user);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [input, setInput] = useState({ otp: '' });
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [timeRemaining, setTimeRemaining] = useState(TIME_REMAINING);
  const [successMessage, setSuccessMessage] = useState('');
  const inputRefs = useRef([]);

  const [timerKey, setTimerKey] = useState(0);
  // Stable snapshot of the phone number — set once when the dialog opens and never
  // changed while it is open, so transient store churn during resend doesn't flicker
  // the subtitle or success message.
  const stablePhoneNumberRef = useRef(phoneNumber);
  const subtitle = stablePhoneNumberRef.current
    ? getPhoneSubtitle(stablePhoneNumberRef.current)
    : EMAIL_SUBTITLE;

  const { error, clearError, isLimitExceeded } = useOTPError();

  const resetOtpFields = () => {
    setOtpDigits(['', '', '', '', '', '']);
    setInput({ otp: '' });
  };

  const focusFirstInput = () => {
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 100);
  };

  const resetDialog = () => {
    setTimeRemaining(TIME_REMAINING);
    setIsVerifying(false);
    setIsResending(false);
    resetOtpFields();
    clearError();
    setSuccessMessage('');
  };

  const handleResendingState = () => {
    setIsResending(true);
  };

  const handleResendCompleteState = () => {
    setIsResending(false);
    setTimeRemaining(TIME_REMAINING);
    setTimerKey((prev) => prev + 1);
    resetOtpFields();
    focusFirstInput();
    setSuccessMessage(phoneNumber ? PHONE_OTP_SENT_MESSAGE : EMAIL_OTP_SENT_MESSAGE);
  };

  const handleVerifyingState = () => {
    setIsVerifying(true);
  };

  const handleVerifyCompleteState = () => {
    setIsVerifying(false);
  };

  const updateLoading = () => {
    const status = envStore.signInSignUpState.status;

    if (RESENDING_STATES.has(status)) {
      handleResendingState();
      return;
    }

    if (VERIFYING_STATES.has(status)) {
      handleVerifyingState();
      return;
    }

    if (VERIFY_COMPLETE_STATES.has(status)) {
      handleVerifyCompleteState();
      return;
    }

    if (RESEND_COMPLETE_STATES.has(status)) {
      handleResendCompleteState();
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // Clear error and success message when user starts typing
    if (!isEmpty(error)) {
      clearError();
    }
    if (successMessage) {
      setSuccessMessage('');
    }

    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value.slice(-1);
    setOtpDigits(newOtpDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtpDigits = [...otpDigits];

    for (let i = 0; i < pastedData.length; i++) {
      newOtpDigits[i] = pastedData[i];
    }

    setOtpDigits(newOtpDigits);

    const nextEmptyIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  const handleVerify = () => {
    setIsVerifying(true);
    clearError();
    onVerify(input.otp);
  };

  const handleResend = () => {
    setIsResending(true);
    clearError();
    resetOtpFields();
    setSuccessMessage('');
    focusFirstInput();
    onResendOTP();
  };

  // Update loading states based on sign in/sign up state
  useEffect(() => {
    updateLoading();
  }, [envStore.signInSignUpState.status]);

  // Reset loading states on auth error
  useEffect(() => {
    const authError = envStore.errorStore.authError;
    if (!authError || Object.keys(authError).length === 0) return;

    const action = Object.keys(authError)[0];

    if (VERIFY_ERROR_ACTIONS.has(action)) {
      setIsVerifying(false);
    } else if (RESEND_ERROR_ACTIONS.has(action)) {
      setIsResending(false);
    }
  }, [envStore.errorStore.authError]);

  // Update input when OTP digits change
  useEffect(() => {
    const otp = otpDigits.join('');
    setInput({ otp });
  }, [otpDigits]);

  // Handle dialog open/close
  useEffect(() => {
    if (!open) return;

    // Snapshot the phone number when the dialog opens so subtitle stays stable
    // even if deliveryDetails changes transiently during the session.
    stablePhoneNumberRef.current = resolveOtpPhoneNumber(
      envStore.signInSignUpState.codeDeliveryDetails,
      userStore.user
    );

    // Clear auth error when dialog opens
    envStore.errorStore.clearAuthError();

    // Focus the first input field after dialog opens
    focusFirstInput();

    // Cleanup when dialog closes
    return () => {
      resetDialog();
    };
  }, [open]);

  // Countdown timer — restarts when dialog opens or after a successful resend
  useEffect(() => {
    if (!open) return;

    const countdownTimer = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownTimer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownTimer);
    };
  }, [open, timerKey]);

  return {
    isVerifying,
    isResending,
    input,
    otpDigits,
    timeRemaining,
    successMessage,
    subtitle,
    inputRefs,
    error,
    isLimitExceeded,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerify,
    handleResend,
  };
};

export default useVerifyOTP;
